/**
 * REST calls for workspace (tenant) administration.
 */
export async function createWorkspace(apiBase, bearerToken, name) {
  const r = await fetch(`${apiBase}/auth/workspaces`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name: (name || '').trim() }),
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    throw new Error(data.message || data.error || r.statusText || 'Create workspace failed');
  }
  return data;
}

export async function updateWorkspaceName(apiBase, bearerToken, tenantId, name) {
  const enc = encodeURIComponent(tenantId);
  const r = await fetch(`${apiBase}/auth/workspaces/${enc}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name: (name || '').trim() }),
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    throw new Error(data.message || data.error || r.statusText || 'Update failed');
  }
  return data;
}

/** One PAGE of members (LOGIN-TO-TENANT.md §8): A→Z by email; ``q`` = email-prefix search; ``recent`` adds the latest
 *  joins (first page only). → ``{members, nextCursor, total, recent?}``. */
export async function listWorkspaceMembers(apiBase, bearerToken, tenantId, { cursor, limit, q, recent } = {}) {
  const enc = encodeURIComponent(tenantId);
  const qs = new URLSearchParams();
  if (cursor) qs.set('cursor', cursor);
  if (limit) qs.set('limit', String(limit));
  if (q) qs.set('q', q);
  if (recent) qs.set('recent', '1');
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const r = await fetch(`${apiBase}/auth/workspaces/${enc}/members${suffix}`, {
    headers: { Authorization: `Bearer ${bearerToken}` },
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    throw new Error(data.message || data.error || r.statusText || 'Failed to load members');
  }
  return data;
}

export async function updateWorkspaceMemberRole(apiBase, bearerToken, tenantId, userSub, { role }) {
  const enc = encodeURIComponent(tenantId);
  const subEnc = encodeURIComponent(userSub);
  const r = await fetch(`${apiBase}/auth/workspaces/${enc}/members/${subEnc}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ role: (role || '').trim().toUpperCase() }),
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    throw new Error(data.message || data.error || r.statusText || 'Could not update role');
  }
  return data;
}

export async function removeWorkspaceMember(apiBase, bearerToken, tenantId, userSub) {
  const enc = encodeURIComponent(tenantId);
  const subEnc = encodeURIComponent(userSub);
  const r = await fetch(`${apiBase}/auth/workspaces/${enc}/members/${subEnc}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${bearerToken}` },
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    throw new Error(data.message || data.error || r.statusText || 'Could not remove member');
  }
  return data;
}

const PENDING_INVITE_STORAGE_KEY = 'stepflow_pending_invite_token';

/** Set when GET /auth/workspaces listed this token in pendingInvites (email-scoped invites). */
export const PENDING_INVITE_TOKEN_SEEN_KEY = 'stepflow_invite_token_seen_in_pending_list';

/** PendingInviteBanner: sessionStorage key for "Hide for now" — clear when an invite is accepted. */
export const INVITE_BANNER_DISMISS_STORAGE_KEY = 'stepflow_invite_banner_dismissed_for';

export function getPendingInviteToken() {
  try {
    return sessionStorage.getItem(PENDING_INVITE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

export function setPendingInviteToken(token) {
  try {
    const t = (token || '').trim();
    if (t) sessionStorage.setItem(PENDING_INVITE_STORAGE_KEY, t);
    else sessionStorage.removeItem(PENDING_INVITE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function clearPendingInviteToken() {
  setPendingInviteToken('');
  try {
    sessionStorage.removeItem(PENDING_INVITE_TOKEN_SEEN_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Create a link invite (optional email — SES sends join URL when email set).
 */
export async function createWorkspaceLinkInvite(apiBase, bearerToken, tenantId, { role, email, add = false } = {}) {
  const enc = encodeURIComponent(tenantId);
  const r = await fetch(`${apiBase}/auth/workspaces/${enc}/invites`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      role: role || 'USER',
      ...(email ? { email: String(email).trim().toLowerCase() } : {}),
      // a colleague on the company's domains is ADDED — no accept step (LOGIN-TO-TENANT.md §7)
      ...(add ? { add: true } : {}),
    }),
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    throw new Error(data.message || data.error || r.statusText || 'Create invite failed');
  }
  return data;
}

/** One PAGE of pending invites → ``{invites, nextCursor}``. */
export async function listWorkspaceInvites(apiBase, bearerToken, tenantId, { cursor, limit } = {}) {
  const enc = encodeURIComponent(tenantId);
  const qs = new URLSearchParams();
  if (cursor) qs.set('cursor', cursor);
  if (limit) qs.set('limit', String(limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const r = await fetch(`${apiBase}/auth/workspaces/${enc}/invites${suffix}`, {
    headers: { Authorization: `Bearer ${bearerToken}` },
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    throw new Error(data.message || data.error || r.statusText || 'Failed to list invites');
  }
  return data;
}

export async function revokeWorkspaceInvite(apiBase, bearerToken, tenantId, token) {
  const enc = encodeURIComponent(tenantId);
  const tok = encodeURIComponent(token);
  const r = await fetch(`${apiBase}/auth/workspaces/${enc}/invites/${tok}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${bearerToken}` },
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    throw new Error(data.message || data.error || r.statusText || 'Revoke failed');
  }
  return data;
}

export async function acceptWorkspaceInvite(apiBase, bearerToken, token) {
  const r = await fetch(`${apiBase}/auth/invites/accept`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token: (token || '').trim() }),
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    const err = new Error(data.message || data.error || r.statusText || 'Could not accept invite');
    err.status = r.status;
    err.code = data.code;
    err.inviteEmail = data.inviteEmail;
    err.signedInEmail = data.signedInEmail;
    throw err;
  }
  return data;
}

export async function rejectWorkspaceInvite(apiBase, bearerToken, token) {
  const r = await fetch(`${apiBase}/auth/invites/reject`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token: (token || '').trim() }),
  });
  const text = await r.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { message: text };
  }
  if (!r.ok) {
    const err = new Error(data.message || data.error || r.statusText || 'Could not reject invite');
    err.status = r.status;
    err.code = data.code;
    err.inviteEmail = data.inviteEmail;
    err.signedInEmail = data.signedInEmail;
    throw err;
  }
  return data;
}

/** COMPANY ACCESS (LOGIN-TO-TENANT.md §6) — the root owner's two settings. ``null`` when the caller isn't the root owner
 *  (403) so the card simply doesn't show. */
export async function getCompanyAccess(apiBase, bearerToken, tenantId) {
  const r = await fetch(`${apiBase}/auth/org-access?tenantId=${encodeURIComponent(tenantId)}`, {
    headers: { Authorization: `Bearer ${bearerToken}` },
  });
  if (r.status === 403 || r.status === 404) return null;
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || data.error || r.statusText || 'Load failed');
  return data;
}

export async function saveCompanyAccess(apiBase, bearerToken, tenantId, { domains, defaultRole }) {
  const r = await fetch(`${apiBase}/auth/org-access?tenantId=${encodeURIComponent(tenantId)}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${bearerToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ domains, defaultRole }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || data.error || r.statusText || 'Save failed');
  return data;
}

/** COMPANY GROUPS (LOGIN-TO-TENANT.md §9) — the root owner's company views: ``groups`` | ``people`` | ``workspaces``.
 *  ``null`` when the caller isn't the root owner (403) so the card simply doesn't show. */
export async function getCompanySection(apiBase, bearerToken, tenantId, section, { limit, cursor } = {}) {
  const qs = new URLSearchParams({ tenantId });
  if (limit) qs.set('limit', String(limit));
  if (cursor) qs.set('cursor', cursor);
  const r = await fetch(`${apiBase}/auth/org-access/${encodeURIComponent(section)}?${qs}`, {
    headers: { Authorization: `Bearer ${bearerToken}` },
  });
  if (r.status === 403 || r.status === 404) return null;
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || data.error || r.statusText || 'Load failed');
  return data;
}

/** Save a group (``{groupId?, name, rules}``) or move a person (``{sub, groupId}``). */
export async function saveCompanySection(apiBase, bearerToken, tenantId, section, body) {
  const r = await fetch(`${apiBase}/auth/org-access/${encodeURIComponent(section)}?tenantId=${encodeURIComponent(tenantId)}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${bearerToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || data.error || r.statusText || 'Save failed');
  return data;
}

/** FIND PEOPLE TO INVITE (LOGIN-TO-TENANT.md §7) — ``{mode: search | invite_by_email, results: [{email, name, member}],
 *  invite: {email, member} | null}``. A consumer workspace never looks anyone up (no account enumeration). */
export async function searchWorkspaceDirectory(apiBase, bearerToken, tenantId, q) {
  const r = await fetch(
    `${apiBase}/auth/workspaces/${encodeURIComponent(tenantId)}/directory?q=${encodeURIComponent(q || '')}`,
    { headers: { Authorization: `Bearer ${bearerToken}` } });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || data.error || r.statusText || 'Search failed');
  return data;
}

/** Is a stored invite link still OPEN and FOR ME? ``{open, forYou}`` (reveals nothing else). On a network error →
 *  ``null`` (keep the link — never drop a valid invite because of a blip). */
export async function getInviteStatus(apiBase, bearerToken, token) {
  try {
    const r = await fetch(`${apiBase}/auth/invites/status?token=${encodeURIComponent((token || '').trim())}`, {
      headers: { Authorization: `Bearer ${bearerToken}` },
    });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

/** PURE — drop the stored invite link? Only on a definite server answer (used / expired / someone else's). */
export function shouldDropInviteToken(status) {
  return !!status && (status.open === false || status.forYou === false);
}
